import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToCleaningData } from "../../../../redux/cleaningSlice";
import {
  submitCleaningRating,
  fetchCleaningRatingByReservation,
} from "../../../../redux/cleaningRatingSlice";

/* ── green tokens ── */
const G = {
  green:      "#16a34a",
  greenDark:  "#14532d",
  greenLight: "#dcfce7",
  border:     "#bbf7d0",
  muted:      "#6b7280",
  text:       "#14532d",
  surface:    "#ffffff",
  bg:         "#f0fdf4",
};

const CRITERIA = [
  "Cleanliness",
  "Location",
  "Host Communication",
  "Accuracy",
  "Host Service",
  "Check-in",
  "Communication",
  "Value",
];

const CleaningReview: React.FC = () => {
  const { guestId, listingId, reservationId } = useParams<{
    guestId: string;
    listingId: string;
    reservationId: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const listing = useSelector((s: RootState) => s.cleaning.currentListing);

  const [ratings,     setRatings]     = useState<Record<string, number>>({});
  const [guestReview, setGuestReview] = useState("");
  const [reviewed,    setReviewed]    = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [message,     setMessage]     = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [hovered,     setHovered]     = useState<{ category: string; star: number } | null>(null);

  /* ── load listing + existing rating ── */
  useEffect(() => {
    if (listingId) dispatch(listenToCleaningData(listingId));

    if (listingId && reservationId) {
      dispatch(fetchCleaningRatingByReservation({ listingId, reservationId }))
        .unwrap()
        .then((existing) => {
          if (existing) {
            setGuestReview(existing.guestReview || "");
            setRatings({
              "Cleanliness":        existing.cleanliness        || 0,
              "Location":           existing.location           || 0,
              "Host Communication": existing.communication      || 0,
              "Accuracy":           existing.accuracy           || 0,
              "Host Service":       existing.service            || 0,
              "Check-in":           existing.checkInRating      || 0,
              "Communication":      existing.communicationGeneral || 0,
              "Value":              existing.value              || 0,
            });
            setReviewed(true);
          }
        })
        .catch(() => {});
    }
  }, [dispatch, listingId, reservationId]);

  const handleRating = (category: string, star: number) => {
    if (!reviewed) setRatings((prev) => ({ ...prev, [category]: star }));
  };

  const overallRating = (): string => {
    const scores = Object.values(ratings);
    if (scores.length < CRITERIA.length) return "";
    const avg = scores.reduce((s, v) => s + v, 0) / scores.length;
    if (avg === 5)   return "Excellent";
    if (avg >= 4.5)  return "Superb";
    if (avg >= 4)    return "Very Nice";
    if (avg >= 3)    return "Worth It";
    return "Not Worth It";
  };

  const isReady = guestReview.trim() && Object.keys(ratings).length === CRITERIA.length;

  const submit = async () => {
    if (!listingId || !reservationId || !guestId) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const scores     = Object.values(ratings);
      const numericAvg = parseFloat((scores.reduce((s, v) => s + v, 0) / scores.length).toFixed(1));

      await dispatch(
        submitCleaningRating({
          listingId,
          hostId:        listing?.hostId || "",
          reservationId,
          guestId,
          reviewText:    guestReview,
          criteria:      ratings,
          overallRating: overallRating(),
          listingRating: numericAvg,
        })
      ).unwrap();

      setReviewed(true);
      setMessage({ type: "success", text: "✅ Review posted successfully!" });
    } catch (err: any) {
      setMessage({ type: "error", text: err?.message || "❌ Failed to post review." });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── overall label color ── */
  const labelColor = (() => {
    const o = overallRating();
    if (o === "Excellent" || o === "Superb") return G.green;
    if (o === "Very Nice")                   return "#ca8a04";
    if (o === "Worth It")                    return "#ea580c";
    return "#dc2626";
  })();

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <div style={s.page}>
      <div style={s.cardWrapper}>
        <div style={s.card}>

          {/* ── header ── */}
          <div style={s.header}>
            <div style={s.headerIcon}>🧹</div>
            <div>
              <h2 style={s.heading}>Rate Your Cleaning Service</h2>
              <p style={s.subheading}>Help others find great cleaners</p>
            </div>
          </div>

          {/* ── listing preview ── */}
          {listing && (
            <div style={s.listingPreview}>
              {listing.photos?.[0]
                ? <img src={listing.photos[0]} alt="Listing" style={s.previewImg} />
                : <div style={{ ...s.previewImg, background: G.greenLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>🧹</div>
              }
              <div style={{ paddingLeft: 12 }}>
                <h4 style={{ margin: 0, color: G.text, fontSize: 15, fontWeight: 700 }}>
                  {listing.name || listing.title || "Cleaning Service"}
                </h4>
                {listing.address?.city && (
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: G.muted }}>
                    {[listing.address.city, listing.address.country].filter(Boolean).join(", ")}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── divider ── */}
          <div style={s.divider} />

          {/* ── criteria stars ── */}
          {CRITERIA.map((label) => {
            const value      = ratings[label] || 0;
            const hoverValue = hovered?.category === label ? hovered.star : 0;
            const display    = hoverValue || value;

            return (
              <div key={label} style={s.ratingRow}>
                <span style={s.label}>{label}</span>
                <div style={s.stars}>
                  {[1, 2, 3, 4, 5].map((num) => {
                    const filled = display >= num;
                    return (
                      <button
                        key={num}
                        style={{
                          ...s.star,
                          background:    filled ? `linear-gradient(135deg, ${G.green}, ${G.greenDark})` : "transparent",
                          borderColor:   filled ? G.green : G.border,
                          color:         filled ? "#fff" : G.muted,
                          cursor:        reviewed ? "default" : "pointer",
                          transform:     hovered?.category === label && hovered.star === num ? "scale(1.15)" : "scale(1)",
                        }}
                        onMouseEnter={() => !reviewed && setHovered({ category: label, star: num })}
                        onMouseLeave={() => !reviewed && setHovered(null)}
                        onClick={() => handleRating(label, num)}
                      >
                        ★
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* ── overall label ── */}
          {Object.keys(ratings).length === CRITERIA.length && (
            <div style={{ ...s.overallPill, color: labelColor, borderColor: labelColor, background: labelColor + "14" }}>
              {overallRating()}
            </div>
          )}

          <div style={s.divider} />

          {/* ── text review ── */}
          <label style={s.textareaLabel}>Your experience</label>
          <textarea
            placeholder="Describe how the cleaning service went — thoroughness, punctuality, professionalism…"
            value={guestReview}
            onChange={(e) => setGuestReview(e.target.value)}
            disabled={reviewed}
            style={{ ...s.textarea, opacity: reviewed ? 0.7 : 1 }}
            rows={4}
          />

          {/* ── message ── */}
          {message && (
            <div style={{
              background: message.type === "success" ? G.greenLight : "#fee2e2",
              color:      message.type === "success" ? G.greenDark  : "#991b1b",
              border:     `1px solid ${message.type === "success" ? G.border : "#fca5a5"}`,
              padding: "10px 14px", borderRadius: 10, fontSize: 13, fontWeight: 600, textAlign: "center",
            }}>
              {message.text}
            </div>
          )}

          {/* ── submit ── */}
          <button
            disabled={!isReady || reviewed || submitting}
            style={{
              ...s.submitBtn,
              opacity: isReady && !reviewed ? 1 : 0.45,
              cursor:  isReady && !reviewed ? "pointer" : "not-allowed",
            }}
            onClick={submit}
          >
            {submitting ? "Submitting…" : reviewed ? "Already Reviewed" : "Submit Review"}
          </button>

          {/* ── secondary actions ── */}
          <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
            <button style={s.secondaryBtn} onClick={() => navigate(`/aboutme/${guestId}`)}>
              Your Reviews
            </button>
            <button style={s.secondaryBtn} onClick={() => navigate(-1)}>
              ← Go Back
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CleaningReview;

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  page: {
    background:  G.bg,
    minHeight:   "100vh",
    display:     "flex",
    justifyContent: "center",
    alignItems:  "flex-start",
    padding:     "40px 16px 80px",
  },
  cardWrapper: { maxWidth: 520, width: "100%" },
  card: {
    background:   G.surface,
    borderRadius: 20,
    border:       `1px solid ${G.border}`,
    padding:      28,
    display:      "flex",
    flexDirection:"column",
    gap:          16,
    boxShadow:    "0 12px 40px rgba(22,163,74,.1)",
  },

  header:    { display: "flex", alignItems: "center", gap: 14 },
  headerIcon:{ width: 48, height: 48, borderRadius: 14, background: G.greenLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 },
  heading:   { margin: 0, fontSize: 20, fontWeight: 700, color: G.text },
  subheading:{ margin: "4px 0 0", fontSize: 13, color: G.muted },

  listingPreview: { display: "flex", alignItems: "center", background: G.bg, borderRadius: 12, padding: 12, border: `1px solid ${G.border}` },
  previewImg:     { width: 64, height: 64, borderRadius: 10, objectFit: "cover" as const, flexShrink: 0 },

  divider: { height: 1, background: G.border, borderRadius: 1 },

  ratingRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  label:     { fontSize: 13, fontWeight: 500, color: G.text, flex: 1 },
  stars:     { display: "flex", gap: 6 },
  star: {
    width: 30, height: 30, borderRadius: "50%",
    border: "1.5px solid",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 14, transition: "all .15s", background: "transparent",
  },

  overallPill: {
    textAlign:    "center" as const,
    fontWeight:   700,
    fontSize:     15,
    borderRadius: 999,
    border:       "1.5px solid",
    padding:      "8px 0",
    letterSpacing:"0.04em",
  },

  textareaLabel: { fontSize: 12, fontWeight: 600, color: G.text, letterSpacing: "0.04em" },
  textarea: {
    width:        "100%",
    minHeight:    100,
    borderRadius: 12,
    padding:      "10px 14px",
    background:   G.bg,
    border:       `1px solid ${G.border}`,
    color:        G.text,
    resize:       "vertical" as const,
    fontSize:     13,
    lineHeight:   1.6,
    outline:      "none",
    boxSizing:    "border-box" as const,
  },

  submitBtn: {
    width:        "100%",
    padding:      "13px 0",
    background:   `linear-gradient(135deg, ${G.green} 0%, ${G.greenDark} 100%)`,
    color:        "#fff",
    border:       "none",
    borderRadius: 12,
    fontWeight:   700,
    fontSize:     14,
    letterSpacing:"0.03em",
    boxShadow:    "0 4px 14px rgba(22,163,74,.35)",
    transition:   "opacity .2s",
  },

  secondaryBtn: {
    width:        "100%",
    background:   "transparent",
    color:        G.green,
    border:       `1.5px solid ${G.border}`,
    borderRadius: 12,
    padding:      "11px 0",
    fontWeight:   600,
    fontSize:     13,
    cursor:       "pointer",
  },
};
